import { motion } from "framer-motion";

const process = [
  { n: "01", t: "Discovery", d: "A quick call to understand the product, users and constraints." },
  { n: "02", t: "Strategy", d: "Positioning, information architecture and a clear north star." },
  { n: "03", t: "Design", d: "High-fidelity screens, systems and prototypes — updated fast." },
  { n: "04", t: "Handoff", d: "Documented components and a dev-ready file. Or I build it." },
];

const tools = ["Figma", "Framer", "Webflow", "React", "Tailwind", "Motion", "Notion"];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 lg:py-40 border-t border-white/5"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-5"
          >
            <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.04] via-black to-black p-8 md:p-10 overflow-hidden">
              <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-indigo-500/10 blur-3xl pointer-events-none" />
              <div className="relative">
                <p className="text-xs font-mono uppercase tracking-[0.25em] text-zinc-500">
                  Signed
                </p>
                <div
                  className="mt-4 font-display text-6xl md:text-7xl lg:text-8xl font-black tracking-tighter text-white leading-none italic"
                  style={{ fontFamily: "'Outfit', serif", fontStyle: "italic" }}
                  data-testid="about-signature"
                >
                  Alex
                  <br />
                  Wilson<span className="text-indigo-400">.</span>
                </div>
                <div className="mt-10 space-y-4 border-t border-white/5 pt-6">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Based</span>
                    <span className="text-sm text-zinc-200">Remote · USA</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Building since</span>
                    <span className="text-sm text-zinc-200">2023 · Age 14</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Currently</span>
                    <span className="text-sm text-zinc-200">Sophomore · High school</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Status</span>
                    <span className="inline-flex items-center gap-2 text-sm text-emerald-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      Booking new work
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-7"
          >
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-zinc-500">03 / About</p>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white text-balance">
              Hi, I'm Alex — a self-taught designer building the web from my bedroom.
            </h2>
            <p className="mt-6 text-base lg:text-lg text-zinc-400 leading-relaxed max-w-xl">
              I'm a sophomore in high school with two years of hands-on web development behind me.
              I fell in love with pixels early and I've been shipping sites for friends, small businesses
              and my own projects ever since. What I lack in years I make up for in speed, curiosity and care.
            </p>
            <p className="mt-4 text-base lg:text-lg text-zinc-400 leading-relaxed max-w-xl">
              I keep things simple: fast turnarounds, tidy code, honest communication, and design
              that respects your users. If you're a founder, small business or fellow student who needs
              a site that actually looks good — I'd love to build it with you.
            </p>

            <div className="mt-12">
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-zinc-500 mb-6">Tools I use</p>
              <div className="flex flex-wrap gap-2">
                {tools.map((t) => (
                  <span
                    key={t}
                    className="rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs font-mono text-zinc-300"
                    data-testid={`about-tool-${t.toLowerCase()}`}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-16">
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-zinc-500 mb-8">How we'll work together</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {process.map((p) => (
                  <div key={p.n} className="border-l border-white/10 pl-5">
                    <div className="font-mono text-xs text-indigo-400 mb-2">{p.n}</div>
                    <div className="font-display text-lg font-semibold text-white">{p.t}</div>
                    <div className="mt-1 text-sm text-zinc-400 leading-relaxed">{p.d}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
